import React, { useCallback, useEffect, useMemo, useState } from "react";
import { productsApi } from "../../services/api.service";
import { useApp } from "../../store/AppContext";
import { T, Btn, SectionLabel, Field, Input, Textarea, Card, Modal } from "../shared/UI";

export default function ProductsView() {
  const { notify } = useApp();
  const [products, setProducts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [mode, setMode] = useState("create"); // create | edit
  const [form, setForm] = useState({ name: "", description: "" });
  const [formError, setFormError] = useState(null);

  const selected = useMemo(
    () => products.find(p => String(p.id) === String(selectedId)) || null,
    [products, selectedId]
  );

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await productsApi.list();
      const raw = Array.isArray(res.data?.data) ? res.data.data
        : Array.isArray(res.data) ? res.data : [];
      setProducts(raw);
      if (raw.length && !selectedId) setSelectedId(raw[0].id);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to load products";
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [selectedId]);

  useEffect(() => { loadProducts(); }, [loadProducts]);

  const openCreate = () => {
    setMode("create");
    setForm({ name: "", description: "" });
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = () => {
    if (!selected) return;
    setMode("edit");
    setForm({ name: selected.name || "", description: selected.description || "" });
    setFormError(null);
    setModalOpen(true);
  };

  const saveProduct = async () => {
    if (!form.name.trim()) {
      setFormError("Name is required");
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      if (mode === "create") {
        await productsApi.create({ name: form.name.trim(), description: form.description.trim() });
        notify("success", "Product added");
      } else if (selected) {
        await productsApi.update(selected.id, { name: form.name.trim(), description: form.description.trim() });
        notify("success", "Product updated");
      }
      await loadProducts();
      setModalOpen(false);
    } catch (err) {
      const msg = err.response?.data?.message || err.message || "Failed to save product";
      notify("error", msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.listPanel}>
        <div style={styles.listHeader}>
          <SectionLabel>PRODUCTS</SectionLabel>
          <Btn size="sm" variant="primary" onClick={openCreate}>＋ Add</Btn>
        </div>

        {loading && <div style={styles.info}>Loading products…</div>}
        {error && <div style={{ ...styles.info, color: T.sos }}>{error}</div>}
        {!loading && !error && products.length === 0 && (
          <div style={styles.info}>No products yet.</div>
        )}

        <div style={styles.list}>
          {products.map(p => (
            <Card
              key={p.id}
              onClick={() => setSelectedId(p.id)}
              style={{ ...styles.listItem, ...(String(selectedId) === String(p.id) ? styles.listItemActive : {}) }}
            >
              <div style={{ fontWeight: 700, color: T.text, fontSize: 13 }}>{p.name || "Unnamed product"}</div>
              <div style={{ fontSize: 11, color: T.muted, marginTop: 4, lineHeight: 1.4 }}>
                {(p.description || "No description").slice(0, 120)}
              </div>
            </Card>
          ))}
        </div>
      </div>

      <div style={styles.detailPanel}>
        <SectionLabel action={selected && <Btn size="sm" variant="outline" onClick={openEdit}>Edit</Btn>}>
          PRODUCT DETAILS
        </SectionLabel>
        {selected ? (
          <div>
            <div style={styles.detailName}>{selected.name}</div>
            <div style={styles.detailDesc}>{selected.description || "—"}</div>
            <div style={styles.metaRow}>
              <div style={styles.metaItem}>
                <div style={styles.metaLabel}>ID</div>
                <div style={styles.metaVal}>{selected.id}</div>
              </div>
              {selected.createdAt && (
                <div style={styles.metaItem}>
                  <div style={styles.metaLabel}>CREATED</div>
                  <div style={styles.metaVal}>{selected.createdAt}</div>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div style={styles.info}>Select a product to view details.</div>
        )}
      </div>

      <Modal open={modalOpen} onClose={() => setModalOpen(false)} title={mode === "create" ? "Add Product" : "Update Product"}>
        <Field label="NAME" error={formError}>
          <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name" />
        </Field>
        <Field label="DESCRIPTION">
          <Textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="Description" rows={4} />
        </Field>
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <Btn variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Btn>
          <Btn variant="primary" onClick={saveProduct} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Add Product" : "Update Product"}
          </Btn>
        </div>
      </Modal>
    </div>
  );
}

const styles = {
  wrap: { flex: 1, display: "grid", gridTemplateColumns: "320px 1fr", gap: 16, padding: 16, overflow: "hidden" },
  listPanel: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 14, display: "flex", flexDirection: "column", overflow: "hidden" },
  listHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 },
  list: { overflowY: "auto", paddingRight: 4, display: "flex", flexDirection: "column", gap: 8 },
  listItem: { cursor: "pointer" },
  listItemActive: { borderColor: T.acc, boxShadow: `0 0 0 1px ${T.acc}66 inset` },
  detailPanel: { background: T.s1, border: `1px solid ${T.border}`, borderRadius: 12, padding: 18, overflow: "auto" },
  detailName: { fontSize: 18, fontWeight: 700, color: T.text, marginBottom: 8 },
  detailDesc: { fontSize: 12, color: T.muted, lineHeight: 1.6, marginBottom: 18 },
  metaRow: { display: "flex", gap: 10, flexWrap: "wrap" },
  metaItem: { background: T.s2, border: `1px solid ${T.border2}`, borderRadius: 8, padding: "8px 10px", minWidth: 140 },
  metaLabel: { fontSize: 9, color: T.muted2, fontFamily: T.fontMono, letterSpacing: "1px" },
  metaVal: { fontSize: 12, color: T.text, fontFamily: T.fontMono, marginTop: 4 },
  info: { fontSize: 12, color: T.muted, padding: "8px 4px" },
};
